import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import './photobooth.css';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const S3  = process.env.REACT_APP_S3_PATH            || '';
// /faceswap is slow (ML model load + inference, ~2 min on a cold start) and
// routed through a Lambda Function URL instead of the API Gateway `API`
// above, since API Gateway HTTP APIs hard-cap integration timeout at 30s.
const FACESWAP_API = process.env.REACT_APP_PHOTOBOOTH_FACESWAP_URL || API;

export default function PhotoboothAvatars() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate            = useNavigate();

  const [selected,   setSelected]   = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selfieUrl,  setSelfieUrl]  = useState(null);

  // Avatar filenames come from theme data (stored as JSON array in themesdata table)
  const avatars = themeData?.character || themeData?.avtar || [];

  const authHeader = () => ({
    Authorization:  `Bearer ${user?.sessionId}&${user?.organizationId}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!user?.sessionId) return;
    axios
      .post(`${API}/api/photobooth/selfie`, { userId: user.userId }, { headers: authHeader() })
      .then(res => setSelfieUrl(res.data.imageUrl))
      .catch(() => setSelfieUrl(null));
  }, [user?.sessionId]);

  const handleSubmit = async () => {
    setProcessing(true);

    Swal.fire({
      title:              'Processing…',
      text:               'Your KPOP avatar is being created. This may take about 30 seconds.',
      showConfirmButton:  false,
      allowOutsideClick:  false,
      didOpen:            () => Swal.showLoading(),
    });

    try {
      await axios.post(
        `${FACESWAP_API}/api/photobooth/faceswap`,
        { userId: user.userId, avatarName: selected || '' },
        { headers: authHeader(), timeout: 180_000 },
      );
      Swal.close();
      navigate('/photobooth/final');
    } catch (err) {
      const msg = err.response?.data?.error || 'Face swap failed. Please try again.';
      Swal.fire({
        icon:  'error',
        title: 'Processing Failed',
        text:  msg,
      }).then(() => navigate('/photobooth'));
    } finally {
      setProcessing(false);
    }
  };

  const isMobile = window.innerWidth <= 768;
  const bgImg    = themeData
    ? (isMobile ? themeData.background_mob : themeData.background_desk)
    : null;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${S3}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  return (
    <div className="pb-page" style={bgStyle}>
      <div className="pb-avatars-box">
        <h2
          className="pb-title"
          style={{ color: themeData?.landing_page_title_color || themeData?.text_color }}
        >
          {themeData?.main_title || 'Choose Your Avatar'}
        </h2>

        <p style={{ textAlign: 'center', color: themeData?.text_color || '#555', marginBottom: 8 }}>
          Select a K-pop avatar to swap your face onto, or skip to use your own photo.
        </p>

        <div className="pb-avatars-columns">
          {/* Avatar grid — left side */}
          <div className="pb-avatars-left">
            {avatars.length > 0 ? (
              <div className="pb-avatar-grid">
                {avatars.map((av, i) => (
                  <div
                    key={i}
                    className={`pb-avatar-item ${selected === av ? 'selected' : ''}`}
                    onClick={() => setSelected(selected === av ? null : av)}
                  >
                    {selected === av && <div className="pb-tick">✓</div>}
                    <img
                      src={`${S3}avatar/${av}`}
                      alt={`Avatar ${i + 1}`}
                      className="pb-avatar-img"
                      onError={e => { e.target.style.opacity = '0.3'; }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: themeData?.text_color || '#888', padding: '24px 0' }}>
                No avatars available for this theme.
              </p>
            )}
          </div>

          {/* Uploaded photo — right side */}
          <div className="pb-avatars-right">
            <div className="pb-uploaded-label" style={{ color: themeData?.text_color }}>
              Your Photo
            </div>
            {selfieUrl ? (
              <img
                src={selfieUrl}
                alt="Your uploaded photo"
                className="pb-uploaded-img"
              />
            ) : (
              <div
                className="pb-uploaded-placeholder"
                style={{ color: themeData?.text_color }}
              >
                No photo uploaded
              </div>
            )}
          </div>
        </div>

        <div className="pb-submit-row">
        
          <button
            className="pb-btn"
            onClick={handleSubmit}
            disabled={processing}
            style={{
              background: themeData?.button_background
                ? themeData.button_background
                : `linear-gradient(180deg, ${themeData?.button_color}, ${themeData?.button_color})`,
              color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
            }}
          >
            {selected ? 'Swap Face & Continue →' : 'Use My Photo →'}
          </button>
        </div>
      </div>
    </div>
  );
}
